import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import { useAuth } from '../../context/AuthContext';
import { apiGet } from '../../api/api';
import { logOut } from '../../auth';

interface ProfileData {
  name?: string;
  display_pic?: string;
  community?: string;
}

function SettingsRow({ icon, label, onClick, rightContent, danger }: {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  rightContent?: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.875rem',
        width: '100%', background: 'none', border: 'none',
        padding: '0.875rem 1rem', cursor: onClick ? 'pointer' : 'default',
        textAlign: 'left', transition: 'background 0.1s',
      }}
      onMouseEnter={e => { if (onClick) (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'none'; }}
    >
      <div style={{
        width: '34px', height: '34px', borderRadius: '8px',
        backgroundColor: danger ? '#fee2e2' : '#f3f4f6',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        color: danger ? '#dc2626' : '#6b7280',
      }}>
        {icon}
      </div>
      <span style={{ flex: 1, fontSize: '0.95rem', color: danger ? '#dc2626' : '#1a1f36', fontWeight: 400 }}>{label}</span>
      {rightContent ?? (
        onClick && <span style={{ color: '#d1d5db', fontSize: '1rem' }}>›</span>
      )}
    </button>
  );
}

export default function AppSettings() {
  const { authUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (authUser) {
      apiGet<{ data: ProfileData }>('/api_profile', undefined, true)
        .then(res => setProfileData(res.data))
        .catch(() => {});
    }
  }, [authUser]);

  const handleSignOut = async () => {
    if (!window.confirm("You can simply close the tab when you're done. Sign out anyway?")) return;
    setSigningOut(true);
    try {
      await logOut();
      navigate('/groups');
    } catch (e) {
      console.error(e);
    } finally {
      setSigningOut(false);
    }
  };

  const displayName = profileData?.name || userProfile?.name || '';
  const avatar = profileData?.display_pic || userProfile?.picture || null;
  const initial = displayName.charAt(0).toUpperCase() || '?';

  const sections = [
    {
      title: 'Subscription',
      rows: [
        {
          key: 'subscription',
          label: 'Manage Subscription',
          icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          ),
          onClick: undefined as (() => void) | undefined,
          rightContent: <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>Manage in app</span>,
        },
      ],
    },
    {
      title: 'Safety & Legal',
      rows: [
        {
          key: 'blocked',
          label: 'Blocked Users',
          icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
            </svg>
          ),
          onClick: undefined as (() => void) | undefined,
          rightContent: <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>Manage in app</span>,
        },
        {
          key: 'report',
          label: 'Report Content or User',
          icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" />
            </svg>
          ),
          onClick: undefined as (() => void) | undefined,
          rightContent: <span style={{ fontSize: '0.78rem', color: '#9ca3af' }}>Manage in app</span>,
        },
        {
          key: 'terms',
          label: 'Terms of Service',
          icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
            </svg>
          ),
          onClick: () => navigate('/terms-of-service'),
        },
        {
          key: 'privacy',
          label: 'Privacy Policy',
          icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          ),
          onClick: () => navigate('/privacy-policy'),
        },
      ],
    },
    {
      title: 'Need Help?',
      rows: [
        {
          key: 'whatsapp',
          label: 'Chat with us on WhatsApp',
          icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          ),
          onClick: () => window.open('https://wa.me/918888084629?text=' + encodeURIComponent('Hi! I need some help with Sahayy 🙏'), '_blank'),
        },
      ],
    },
    {
      title: 'Account',
      rows: [
        {
          key: 'delete',
          label: 'Delete Account',
          danger: true,
          icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          ),
          onClick: () => navigate('/settings/delete-account'),
        },
      ],
    },
  ];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <AppNav />

      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '2rem 1rem 6rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111', margin: '0 0 1.5rem', letterSpacing: '-0.02em' }}>Settings</h1>

        {/* Profile card */}
        <button
          onClick={() => navigate('/settings/profile')}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            width: '100%', background: '#fff', border: '1px solid #e5e7eb',
            borderRadius: '16px', padding: '2rem 1rem 1.5rem', cursor: 'pointer',
            marginBottom: '1.5rem', boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
            transition: 'box-shadow 0.15s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)'; }}
        >
          {avatar ? (
            <img src={avatar} alt={displayName} style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', marginBottom: '0.75rem' }} />
          ) : (
            <div style={{
              width: '80px', height: '80px', borderRadius: '50%',
              backgroundColor: '#FF385C', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '2rem', fontWeight: 700, marginBottom: '0.75rem',
            }}>
              {initial}
            </div>
          )}
          <span style={{ fontWeight: 600, fontSize: '1.1rem', color: '#111' }}>{displayName || 'Your Name'}</span>
          <span style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.25rem' }}>Edit profile ›</span>
        </button>

        {/* Sections */}
        {sections.map(section => (
          <div key={section.title} style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 0.5rem 0.25rem' }}>
              {section.title}
            </p>
            <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              {section.rows.map((row, idx) => (
                <React.Fragment key={row.key}>
                  {idx > 0 && <div style={{ height: '1px', backgroundColor: '#f0f2f7', margin: '0 1rem' }} />}
                  <SettingsRow
                    icon={row.icon}
                    label={row.label}
                    onClick={row.onClick}
                    danger={'danger' in row ? (row as any).danger : false}
                    rightContent={'rightContent' in row ? (row as any).rightContent : undefined}
                  />
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}

        {/* Sign Out */}
        <button
          onClick={handleSignOut}
          disabled={signingOut}
          style={{
            display: 'block', width: '100%', background: 'none', border: 'none',
            padding: '1rem', marginTop: '1rem', color: '#FF385C',
            fontSize: '1rem', fontWeight: 500, cursor: 'pointer', textAlign: 'center',
          }}
        >
          {signingOut ? 'Signing out...' : 'Log Out'}
        </button>

        <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#d1d5db', marginTop: '1rem' }}>
          Sahayy Web
        </p>
      </div>
    </div>
  );
}
